import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  approveLeave,
  getAllEmployees,
  getEmployeesBasedOnRole,
  rejectLeave,
} from "../utils/methods";
import { strings } from "../utils";
import { Button } from "../components";
import ModalScreen from "../components/modal.jsx";
import LinkIcon from "../components/Icons/linkIcon.jsx";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import usePersistData from "../store/usePersistData.js";
import moment from "moment/moment.js";

const LeaveApproval = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedRejectEmployeeId, setSelectedRejectEmployeeId] =
    useState(null);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allLeaves, setAllLeaves] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.ceil(allLeaves.length / pageSize);
  const navigate = useNavigate();
  const { user } = usePersistData();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query") || "";

  useEffect(() => {
    const fetchEmployees = async () => {
      // setLoading(true);
      try {
        const employeeData = await getEmployeesBasedOnRole(user.email);
        const filtered = employeeData.filter((employee) => {
          if (!employee.email || !employee.designation) {
            return false;
          }
          return (
            employee.firstName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            employee.designation
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          );
        });

        setEmployees(filtered || []);

        const allFlattenedLeaves = employeeData.reduce((acc, employee) => {
          const employeeLeaves = employee.leaves.map((leave) => ({
            ...leave,
            employeeId: employee.id,
            firstName: employee.firstName,
            email: employee.email,
          }));
          return [...acc, ...employeeLeaves];
        }, []);

        const sortedFetchedLeaves = allFlattenedLeaves.sort((a, b) => {
          const timestampA = a.createdAt.seconds;
          const timestampB = b.createdAt.seconds;
          return timestampB - timestampA;
        });

        setAllLeaves(sortedFetchedLeaves);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching Employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [searchQuery]);

  const paginatedLeaves = allLeaves.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handlePreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    if (page * pageSize < allLeaves.length) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleClickOpen = (employeeId, leaveId) => {
    setSelectedEmployeeId(employeeId);
    setSelectedLeaveId(leaveId);
    setOpenDialog(true);
  };

  const handleClickRejectOpen = (employeeId, leaveId) => {
    setSelectedRejectEmployeeId(employeeId);
    setSelectedLeaveId(leaveId);
    setOpenRejectDialog(true);
  };

  const handleClickDetailsOpen = (employee, leaveId) => {
    const leaveDetails = employee.leaves.find(
      (leave) => leave.leaveId === leaveId
    );
    if (!leaveDetails) {
      toast.error("Leave details not found.");
      return;
    }

    setSelectedEmployeeId(employee.id);
    setSelectedLeaveId(leaveId);
    setSelectedEmployeeDetails({ ...employee, leave: leaveDetails });
    setOpenDetailsDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedEmployeeId(null);
  };

  const handleRejectClose = () => {
    setOpenRejectDialog(false);
    setSelectedRejectEmployeeId(null);
  };

  const handleDetailsClose = () => {
    setOpenDetailsDialog(false);
    setSelectedEmployeeId(null);
    setSelectedEmployeeDetails(null);
  };

  const getLeaveTypeClass = (leaveType) => {
    switch (leaveType) {
      case "Annual Leave":
        return "bg-color-13 text-color-12";
      case "Sick Leave":
        return "bg-color-10 text-color-11";
      case "Casual Leave":
        return "bg-color-14 text-color-15";
      default:
        return "bg-color-1 text-white";
    }
  };

  const getLeaveStatusClass = (status) => {
    switch (status) {
      case "Approved":
        return "bg-color-10 text-color-11";
      case "Rejected":
        return "bg-color-13 text-color-12";
      default:
        return "bg-gray-400 text-white";
    }
  };

  const calculateLeaveDays = (startingDate, endingDate) => {
    const start = new Date(startingDate);
    const end = new Date(endingDate);
    const differenceInTime = end - start;
    return differenceInTime / (1000 * 3600 * 24) + 1;
  };

  useEffect(() => {}, [allLeaves, page]);

  const handleApproveLeave = async () => {
    try {
      if (!selectedEmployeeId || !selectedLeaveId) {
        toast.error("Employee or leave selection is invalid.");
        return;
      }

      const employee = employees.find((emp) => emp.id === selectedEmployeeId);
      if (!employee) {
        toast.error("Employee data not found.");
        return;
      }

      const leaveData = employee.leaves?.find(
        (leave) => leave.leaveId === selectedLeaveId
      );
      if (!leaveData) {
        toast.error("Leave data not found.");
        return;
      }

      const { leaveType, leaveStartingDate, leaveEndingDate } = leaveData;
      const leaveDays = calculateLeaveDays(leaveStartingDate, leaveEndingDate);
      const leaveBalance = employee.leaveBalance;

      // Insufficient balance check and approval logic
      if (leaveBalance && leaveBalance.totalLeaves >= leaveDays) {
        const updatedLeaveBalance = { ...leaveBalance };
        if (leaveType === "Annual Leave") {
          updatedLeaveBalance.al -= leaveDays;
        } else if (leaveType === "Sick Leave") {
          updatedLeaveBalance.sl -= leaveDays;
        } else if (leaveType === "Casual Leave") {
          updatedLeaveBalance.cl -= leaveDays;
        }
        updatedLeaveBalance.totalLeaves -= leaveDays;

        await approveLeave(employee.id, leaveData.leaveId, updatedLeaveBalance);

        // Update employees and allLeaves state to trigger a re-render
        setEmployees((prevEmployees) =>
          prevEmployees.map((emp) =>
            emp.id === employee.id
              ? {
                  ...emp,
                  leaveBalance: updatedLeaveBalance,
                  leaves: emp.leaves.map((leave) =>
                    leave.leaveId === selectedLeaveId
                      ? { ...leave, status: "Approved" }
                      : leave
                  ),
                }
              : emp
          )
        );

        // Update allLeaves state with the new leave status
        setAllLeaves((prevLeaves) =>
          prevLeaves.map((leave) =>
            leave.leaveId === selectedLeaveId
              ? { ...leave, status: "Approved" }
              : leave
          )
        );

        toast.success("Leave approved successfully.");
        handleClose();
      }
    } catch (error) {
      console.error("Error approving leave:", error);
      toast.error("An error occurred while approving the leave.");
    }
  };

  const handleRejectLeave = async () => {
    const employee = employees.find(
      (emp) => emp.id === selectedRejectEmployeeId
    );

    if (!employee) {
      console.error("Employee data is missing for the selected employee.");
      return;
    }

    const leaveData = employee.leaves?.find(
      (leave) => leave.leaveId === selectedLeaveId
    );

    if (!leaveData) {
      console.error("Leave data or leaveId is missing for the selected leave.");
      return;
    }
    const { leaveId } = leaveData;

    try {
      await rejectLeave(employee.id, leaveId);

      // Update employees and allLeaves state
      setEmployees((prevEmployees) =>
        prevEmployees.map((emp) =>
          emp.id === employee.id
            ? {
                ...emp,
                leaves: emp.leaves.map((leave) =>
                  leave.leaveId === leaveId
                    ? { ...leave, status: "Rejected" }
                    : leave
                ),
              }
            : emp
        )
      );

      setAllLeaves((prevLeaves) =>
        prevLeaves.map((leave) =>
          leave.leaveId === leaveId ? { ...leave, status: "Rejected" } : leave
        )
      );

      handleRejectClose();
    } catch (error) {
      console.error("Error rejecting leave:", error);
    }
  };

  return (
    <div className="container py-8 h-full bg-border overflow-scroll px-[22px]">
      <p className="text-[32px] font-semibold pb-6">
        {strings["leavePageTitle"]}
      </p>
      <div className="flex justify-end items-center space-x-4 mt-4">
        <Button
          variant="underlineBold"
          className={`px-4 py-2 underline text-black font-bold ${
            page === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
          onClick={handlePreviousPage}
          disabled={page === 1}
        >
          Previous
        </Button>
        <span>{`Page ${page} of ${totalPages}`}</span>
        <Button
          variant="underlineBold"
          className={`px-4 py-2 underline text-black font-bold ${
            page * pageSize >= allLeaves.length
              ? "text-gray-400 cursor-not-allowed"
              : ""
          }`}
          onClick={handleNextPage}
          disabled={page * pageSize >= allLeaves.length}
        >
          Next
        </Button>
      </div>
      <TableContainer component={Paper} sx={{ border: "2px solid border" }}>
        <Table sx={{ minWidth: 670 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="center">Leave Type</TableCell>
              <TableCell align="center">Leave Detail</TableCell>
              <TableCell align="center">Total Leaves</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : paginatedLeaves.length > 0 ? (
            paginatedLeaves.map((leave) => {
              const {
                employeeId,
                leaveId,
                leaveStartingDate,
                leaveEndingDate,
                leaveType = "Others",
                status,
              } = leave;

              const employee = employees.find((emp) => emp.id === employeeId);

              if (!employee) return null;

              return (
                <TableRow
                  key={`${employee.id}-${leaveId}`}
                  onClick={() => navigate(`/leaveRequest/${employee.id}`)}
                  style={{ cursor: "pointer", width: "100%" }}
                >
                  <TableCell align="center">
                    {employee.firstName || "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    {employee.email || "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    {moment(leaveStartingDate).format("MMM DD, YYYY")} to{" "}
                    {moment(leaveEndingDate).format("MMM DD, YYYY")}
                  </TableCell>

                  <TableCell align="center">
                    <span
                      className={`px-1 py-1 rounded-[60px] block w-[100%] text-center mx-auto ${getLeaveTypeClass(
                        leaveType
                      )}`}
                    >
                      {leaveType}
                    </span>
                  </TableCell>

                  <TableCell align="center" sx={{ width: "20%" }}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClickDetailsOpen(employee, leaveId);
                      }}
                      variant="underlineBold"
                    >
                      {strings["detailBtn"]}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    {employee.leaveBalance?.totalLeaves || "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    <div className="flex items-center justify-center gap-x-2">
                      {status !== "Approved" && status !== "Rejected" ? (
                        <>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickOpen(employee.id, leaveId);
                            }}
                          >
                            {strings["approveBtn"]}
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickRejectOpen(employee.id, leaveId);
                            }}
                            variant="outlineArrow"
                          >
                            {strings["rejectBtn"]}
                          </Button>
                        </>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded-[60px] ${getLeaveStatusClass(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={9} align="center">
                No record found.
              </TableCell>
            </TableRow>
          )}
        </Table>
      </TableContainer>
      <div className="flex justify-end items-center space-x-4 mt-4">
        <Button
          variant="underlineBold"
          className={`px-4 py-2 underline text-black font-bold ${
            page === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
          onClick={handlePreviousPage}
          disabled={page === 1}
        >
          Previous
        </Button>
        <span>{`Page ${page} of ${totalPages}`}</span>
        <Button
          variant="underlineBold"
          className={`px-4 py-2 underline text-black font-bold ${
            page * pageSize >= allLeaves.length
              ? "text-gray-400 cursor-not-allowed"
              : ""
          }`}
          onClick={handleNextPage}
          disabled={page * pageSize >= allLeaves.length}
        >
          Next
        </Button>
      </div>
      {/* Modals for approve, reject, and details */}
      <ModalScreen
        open={openDialog}
        onClose={handleClose}
        onConfirm={handleApproveLeave}
        title={strings["approveTitle"]}
        description={strings["approveLeaveDesc"]}
        cancelButtonText={strings["cancelBtn"]}
        confirmButtonText={strings["approveBtn"]}
        confirmButtonClasses="!bg-primary"
        cancelButtonClasses="!bg-border"
        hideClass="hidden"
      />
      <ModalScreen
        open={openRejectDialog}
        onClose={handleRejectClose}
        onConfirm={handleRejectLeave}
        title={strings["rejectTitle"]}
        description={strings["rejectLeaveDesc"]}
        cancelButtonText={strings["cancelBtn"]}
        confirmButtonText={strings["rejectBtn"]}
        hideClass="hidden"
      />
      <ModalScreen
        open={openDetailsDialog}
        onClose={handleDetailsClose}
        title={strings["detailsTitle"]}
        description={
          selectedEmployeeDetails?.leave
            ? `Details for ${selectedEmployeeDetails.firstName}: ${
                selectedEmployeeDetails.leave.leaveType
              } Leave from ${
                selectedEmployeeDetails.leave.leaveStartingDate
              } to ${selectedEmployeeDetails.leave.leaveEndingDate}  ${
                selectedEmployeeDetails?.leave.leaveDetails || ""
              }`
            : "No details available."
        }
        onConfirm={handleApproveLeave}
        cancelButtonText={strings["cancelBtn"]}
        confirmButtonText={strings["approveBtn"]}
        linkIcon={LinkIcon}
        certificate={strings["medicalCertificate"]}
        download={selectedEmployeeDetails?.leave.details}
        confirmButtonClasses="!bg-primary"
        cancelButtonClasses="!bg-border"
      />
    </div>
  );
};

export default LeaveApproval;
